import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'

export interface ActivityEntry {
  id: number
  app_id: string
  timestamp: string
  log_type: string
  message: string
  detail: string | null
  detail_label: string | null
  expandable: boolean
  container_id: string | null
  task_id: string | null
}

export interface ContainerInfo {
  container_id: string
  name: string
  status: string
  error_message: string | null
  created_at: string
  last_event_at: string | null
  fly_machine_id: string | null
  active_task_id: string | null
  prompt: string | null
}

export interface TaskInfo {
  id: string
  skill: string
  subtasks: string[]
  prompt: string | null
  status: string
  container_id: string | null
  created_at: string
  started_at: string | null
  completed_at: string | null
  log_count: number
  parent_task_id: string | null
  parent_skill: string | null
  required_task_ids: string[] | null
}

interface ActivityState {
  tasks: TaskInfo[]
  containers: ContainerInfo[]
  appActivity: ActivityEntry[]
  taskLogs: Record<string, ActivityEntry[]>
  taskLogsLoading: Record<string, boolean>
  containerLogs: Record<string, ActivityEntry[]>
  containerLogsLoading: Record<string, boolean>
  polling: boolean
  lastFetchedAt: string | null
  loading: boolean
  error: string | null
}

const initialState: ActivityState = {
  tasks: [],
  containers: [],
  appActivity: [],
  taskLogs: {},
  taskLogsLoading: {},
  containerLogs: {},
  containerLogsLoading: {},
  polling: false,
  lastFetchedAt: null,
  loading: false,
  error: null,
}

let activityFetchInFlight = false

export const fetchActivity = createAsyncThunk(
  'activity/fetchActivity',
  async (appId: string) => {
    const response = await fetch(`/api/activity/${appId}`)
    if (!response.ok) throw new Error('Failed to fetch activity')
    const data = await response.json()
    // Support both old format (array) and new format
    if (Array.isArray(data)) {
      return { tasks: [] as TaskInfo[], containers: [] as ContainerInfo[], appActivity: data as ActivityEntry[] }
    }
    return {
      tasks: (data.tasks || []) as TaskInfo[],
      containers: (data.containers || []) as ContainerInfo[],
      appActivity: (data.appActivity || data.entries || []) as ActivityEntry[],
    }
  },
  {
    condition: () => {
      if (activityFetchInFlight) return false
      activityFetchInFlight = true
      return true
    },
  },
)

export const fetchActivityIncremental = createAsyncThunk(
  'activity/fetchActivityIncremental',
  async ({ appId, since }: { appId: string; since: string }) => {
    const response = await fetch(`/api/activity/${appId}?since=${encodeURIComponent(since)}`)
    if (!response.ok) throw new Error('Failed to fetch activity')
    const data = await response.json()
    return {
      tasks: (data.tasks || []) as TaskInfo[],
      containers: (data.containers || []) as ContainerInfo[],
      appActivity: (data.appActivity || data.entries || []) as ActivityEntry[],
    }
  },
  {
    condition: () => {
      if (activityFetchInFlight) return false
      activityFetchInFlight = true
      return true
    },
  },
)

export const fetchTaskLogs = createAsyncThunk(
  'activity/fetchTaskLogs',
  async ({ appId, taskId }: { appId: string; taskId: string }) => {
    const response = await fetch(`/api/activity/${appId}?task_id=${taskId}`)
    if (!response.ok) throw new Error('Failed to fetch task logs')
    const data = await response.json()
    return { taskId, logs: (data.logs || []) as ActivityEntry[] }
  },
)

export const fetchContainerLogs = createAsyncThunk(
  'activity/fetchContainerLogs',
  async ({ appId, containerId }: { appId: string; containerId: string }) => {
    const response = await fetch(`/api/activity/${appId}?container_id=${containerId}`)
    if (!response.ok) throw new Error('Failed to fetch container logs')
    const data = await response.json()
    return { containerId, logs: (data.logs || []) as ActivityEntry[] }
  },
)

const activitySlice = createSlice({
  name: 'activity',
  initialState,
  reducers: {
    clearActivity() {
      return initialState
    },
    setPolling(state, action: { payload: boolean }) {
      state.polling = action.payload
    },
    removeTask(state, action: { payload: string }) {
      state.tasks = state.tasks.filter((t) => t.id !== action.payload)
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchActivity.pending, (state) => {
        if (state.tasks.length === 0 && state.appActivity.length === 0) {
          state.loading = true
        }
        state.error = null
      })
      .addCase(fetchActivity.fulfilled, (state, action) => {
        activityFetchInFlight = false
        state.loading = false
        state.lastFetchedAt = new Date().toISOString()
        state.tasks = action.payload.tasks
        state.containers = action.payload.containers
        state.appActivity = action.payload.appActivity
      })
      .addCase(fetchActivity.rejected, (state, action) => {
        activityFetchInFlight = false
        state.loading = false
        state.error = action.error.message || 'Failed to fetch activity'
      })
      .addCase(fetchActivityIncremental.fulfilled, (state, action) => {
        activityFetchInFlight = false
        state.lastFetchedAt = new Date().toISOString()
        // Merge updated tasks: replace existing by id, add new ones
        const taskMap = new Map(state.tasks.map((t) => [t.id, t]))
        for (const t of action.payload.tasks) taskMap.set(t.id, t)
        state.tasks = Array.from(taskMap.values())
        // Merge updated containers: replace existing by id, add new ones
        const containerMap = new Map(state.containers.map((c) => [c.container_id, c]))
        for (const c of action.payload.containers) containerMap.set(c.container_id, c)
        state.containers = Array.from(containerMap.values())
        // Prepend new activity entries (deduplicated by id)
        if (action.payload.appActivity.length > 0) {
          const existingIds = new Set(state.appActivity.map((a) => a.id))
          const newEntries = action.payload.appActivity.filter((a) => !existingIds.has(a.id))
          state.appActivity = [...newEntries, ...state.appActivity]
        }
      })
      .addCase(fetchActivityIncremental.rejected, () => {
        activityFetchInFlight = false
      })
      .addCase(fetchTaskLogs.pending, (state, action) => {
        state.taskLogsLoading[action.meta.arg.taskId] = true
      })
      .addCase(fetchTaskLogs.fulfilled, (state, action) => {
        state.taskLogsLoading[action.payload.taskId] = false
        state.taskLogs[action.payload.taskId] = action.payload.logs
      })
      .addCase(fetchTaskLogs.rejected, (state, action) => {
        state.taskLogsLoading[action.meta.arg.taskId] = false
      })
      .addCase(fetchContainerLogs.pending, (state, action) => {
        state.containerLogsLoading[action.meta.arg.containerId] = true
      })
      .addCase(fetchContainerLogs.fulfilled, (state, action) => {
        state.containerLogsLoading[action.payload.containerId] = false
        state.containerLogs[action.payload.containerId] = action.payload.logs
      })
      .addCase(fetchContainerLogs.rejected, (state, action) => {
        state.containerLogsLoading[action.meta.arg.containerId] = false
      })
  },
})

export const { clearActivity, setPolling, removeTask } = activitySlice.actions
export default activitySlice.reducer
